import { type InputHTMLAttributes, forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';

export type InputType = 'text' | 'email' | 'password' | 'number';

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'type'
> {
  type?: InputType;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      placeholder,
      value,
      onChange,
      error,
      disabled,
      className,
      id: providedId,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const errorId = `${id}-error`;

    return (
      <div className="w-full">
        <input
          ref={ref}
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors',
            'placeholder:text-[#999]',
            'focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]',
            'disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-[#999]',
            error
              ? 'border-[#c62828] focus:border-[#c62828] focus:ring-[#c62828]'
              : 'border-[#e0e0e0]',
            className,
          )}
          {...props}
        />
        {error && (
          <p id={errorId} className="mt-1.5 text-xs text-[#c62828]" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
